import type { CustomerType } from "@/api.types.v2";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSession } from "@/services/auth0/session";
import axios from "axios";
import { customerKeys } from "./customer-keys";
import {
  customerReadPolicy,
  useCustomerReadRecovery,
  useCustomerQueryGuard,
} from "./customer-read-policy";

export interface CustomerIdentity {
  id: string;
  customerNumber: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  type: CustomerType;
  status: string;
}
export interface CustomerListRow extends CustomerIdentity {
  phone: string | null;
  isTestAccount: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  emailVerifiedAt: string | null;
  phoneVerifiedAt: string | null;
  businessAccount: {
    id: string;
    companyName: string;
    taxNumber: string | null;
  } | null;
  carrierConnections: {
    carrier: { name: string };
    tier: { name: string } | null;
  }[];
  billing: { id: string } | null;
  allowedPaymentTermsIds: string[];
  netTermsCreditLimit: number;
  creditBalance: { balanceCents: number; frozen: boolean };
}
export interface CustomerPage {
  data: CustomerListRow[];
  pagination: {
    totalCount: number;
    totalPages: number;
    page: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}
interface PageOptions {
  enabled?: boolean;
  limit?: number;
  page?: number;
  search?: string;
}
interface CustomerListOptions extends PageOptions {
  type?: CustomerType;
  status?: string;
  sortBy?: "createdAt" | "updatedAt";
  sortOrder?: "asc" | "desc";
}

export function useCustomers(options: CustomerListOptions = {}) {
  const { token, user } = useSession();
  const identity = token && user ? user.id : null;
  const {
    page = 1,
    limit = 10,
    search = "",
    enabled = true,
    ...filters
  } = options;
  const params = { page, limit, search, ...filters };
  const scope = JSON.stringify([identity, limit, search, filters]);
  const queryKey = customerKeys.list(identity, params);
  const guard = useCustomerQueryGuard(queryKey);
  const query = useQuery<CustomerPage, Error>({
    queryKey,
    queryFn: ({ signal }) =>
      guard(async () => {
        const { data } = await axios.get<CustomerPage>(
          `${import.meta.env.VITE_API_URL}/api/v2/customers`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
            signal,
          }
        );
        return data;
      }),
    ...customerReadPolicy,
    enabled: identity !== null && enabled,
  });
  const [lastLoaded, setLastLoaded] = useState<{
    scope: string;
    data: CustomerPage;
  }>();
  useEffect(() => {
    if (identity === null) setLastLoaded(undefined);
    else if (query.data) setLastLoaded({ scope, data: query.data });
  }, [identity, scope, query.data]);
  const recovery = useCustomerReadRecovery(query.error, query.errorUpdatedAt);
  return {
    ...query,
    ...recovery,
    identity,
    data:
      identity === null
        ? undefined
        : (query.data ??
          (lastLoaded?.scope === scope ? lastLoaded.data : undefined)),
    refetch: () => {
      if (identity !== null && Date.now() >= recovery.retryAt)
        return query.refetch();
    },
  };
}
export function useCustomerIdentity(customerId?: string | null) {
  const { token, user } = useSession();
  const identity = token && user ? user.id : null;
  const queryKey = customerKeys.identity(identity, customerId ?? null);
  const guard = useCustomerQueryGuard(queryKey);
  const query = useQuery<CustomerIdentity, Error>({
    queryKey,
    queryFn: ({ signal }) =>
      guard(async () => {
        const { data } = await axios.get<CustomerIdentity>(
          `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}`,
          { headers: { Authorization: `Bearer ${token}` }, signal }
        );
        // Store only identity fields, not the detail endpoint's billing payload.
        return {
          id: data.id,
          customerNumber: data.customerNumber,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          type: data.type,
          status: data.status,
        };
      }),
    ...customerReadPolicy,
    enabled: identity !== null && !!customerId,
  });
  const recovery = useCustomerReadRecovery(query.error, query.errorUpdatedAt);
  return {
    ...query,
    ...recovery,
    data: identity === null ? undefined : query.data,
    refetch: () => {
      if (identity !== null && customerId && Date.now() >= recovery.retryAt)
        return query.refetch();
    },
  };
}

export function useSyncCustomerWithStripe() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId }: { customerId: string }) => {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/sync-with-stripe`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      // Invalidate customers query to refetch with updated data
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useUpdateCustomerPaymentAccess() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      allowedPaymentTermsIds,
      netTermsCreditLimit,
    }: {
      customerId: string;
      allowedPaymentTermsIds?: string[];
      netTermsCreditLimit?: number;
    }) => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/payment-access`,
        {
          allowedPaymentTermsIds,
          netTermsCreditLimit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useDeleteBusinessAccount() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      businessAccountId,
    }: {
      customerId: string;
      businessAccountId: string;
    }) => {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/business-accounts/${businessAccountId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useUpdateCustomerTestAccount() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      isTestAccount,
    }: {
      customerId: string;
      isTestAccount: boolean;
    }) => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}`,
        { isTestAccount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}
